import { PrincipalIdSchema, ServiceIdSchema } from "@core/domains/identity";
import { identity } from "@core/foundation/function";
import { ulid } from "@core/foundation/id";
import {
  createModel,
  createModelId,
  createModelSchemas,
} from "@core/foundation/model";
import { z } from "@core/zod";

import { ID_PREFIX } from "./shared/id-prefix.ts";

export const MediaAssetRenditionSchema = z.enum(["original", "preview"]);

export type MediaAssetRendition = z.infer<typeof MediaAssetRenditionSchema>;

const MediaAssetStatusSchema = z.enum(["awaitingUpload", "uploaded"]);

type MediaAssetStatus = z.infer<typeof MediaAssetStatusSchema>;

export const MediaAssetVisibilitySchema = z.enum(["private", "public"]);

export type MediaAssetVisibility = z.infer<typeof MediaAssetVisibilitySchema>;

const { generateId: generateMediaAssetId, idSchema: MediaAssetIdSchema } =
  createModelId({ digestFn: ulid, idPrefix: ID_PREFIX.MEDIA_ASSET });

export { MediaAssetIdSchema };

export type MediaAssetId = z.infer<typeof MediaAssetIdSchema>;

const MediaAssetMetadataSchema = z.record(z.string(), z.unknown());

export const MediaAsset = createModel({
  create: (item, schema) => {
    const status: MediaAssetStatus = "awaitingUpload";

    return { ...schema.decode(item), id: generateMediaAssetId(), status };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        contentType: z.string(),
        fileName: z.string(),
        hasPreview: z.boolean(),
        height: z.int().positive(),
        id: MediaAssetIdSchema,
        metadata: MediaAssetMetadataSchema,
        metadataPrincipalId: ServiceIdSchema,
        principalId: PrincipalIdSchema,
        sizeBytes: z.int().nonnegative().optional(),
        status: MediaAssetStatusSchema,
        visibility: MediaAssetVisibilitySchema,
        width: z.int().positive(),
      },
      {
        contentType: "The file's MIME type.",
        fileName: "The file's name.",
        hasPreview: "Whether the asset's WebP preview is stored.",
        height: "The image's height in pixels.",
        id: "The unique identifier for the asset.",
        metadata: "The data the authoring service attached to the asset.",
        metadataPrincipalId:
          "The id of the service that authored the metadata.",
        principalId: "The id of the principal the asset belongs to.",
        sizeBytes: "The file's size in bytes, recorded once the upload ends.",
        status: "The asset's status, either awaitingUpload or uploaded.",
        visibility: "The asset's visibility, either private or public.",
        width: "The image's width in pixels.",
      },
    ),
    defaults: { hasPreview: false, metadata: {} },
    derive: {
      create: schema =>
        schema.omit({ id: true, sizeBytes: true, status: true }),
      recovery: identity,
      update: schema =>
        schema
          .pick({ hasPreview: true, sizeBytes: true, status: true })
          .partial(),
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface MediaAsset extends z.infer<typeof MediaAsset.schema> {}
